import {
  HealthPlanCardStatus,
  HealthPlanCardUploadErrorCode,
  OcrStatus,
  UploadStatus,
} from '../types/healthPlanCardUpload'

export const HEALTH_PLAN_OCR_POLL_INTERVAL_MS = 5000 // 5 seconds
export const HEALTH_PLAN_OCR_MAX_POLL_ATTEMPTS = 6 // 30 seconds total before timeout

export const isHealthPlanCardProcessingStatus = (status: HealthPlanCardStatus): boolean => {
  return (
    status === UploadStatus.UPLOAD_PROCESSING || status === UploadStatus.UPLOADED || status === OcrStatus.OCR_PROCESSING
  )
}

export const isHealthPlanCardTerminalStatus = (status: HealthPlanCardStatus): boolean => {
  return status === OcrStatus.OCR_COMPLETED || status === OcrStatus.OCR_FAILED || status === UploadStatus.UPLOAD_FAILED
}

export interface UploadStatusValidationResult {
  isValid: boolean
  errorCode?: string
  errorMessage?: string
}

export const validateUploadResponseStatus = (
  status: HealthPlanCardStatus,
  error?: { code?: string; message?: string },
): UploadStatusValidationResult => {
  if (status === UploadStatus.UPLOAD_FAILED) {
    return {
      isValid: false,
      errorCode: error?.code || HealthPlanCardUploadErrorCode.UPLOAD_ERROR,
      errorMessage: error?.message || 'Upload failed',
    }
  }

  // Valid upload responses are UPLOAD_PROCESSING (initial) or UPLOADED (after S3 upload)
  if (status !== UploadStatus.UPLOAD_PROCESSING && status !== UploadStatus.UPLOADED) {
    return {
      isValid: false,
      errorCode: 'UNEXPECTED_STATUS',
      errorMessage: `Unexpected upload status: ${status}`,
    }
  }

  return { isValid: true }
}

export const BOTH_CARDS_UPLOADED_TOAST_DELAY_MS = 5000

export interface BothCardsUploadedCheckParams {
  isFrontCardProcessed: boolean
  isBackCardProcessed: boolean
  toastTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
  onBothCardsUploaded: () => void
}

/**
 * Checks if both front and back health plan cards have been successfully uploaded and scanned.
 * If both cards are processed, triggers the "both cards uploaded" toast notification after a delay.
 *
 * This function should be called after each individual card upload completes (both success and failure cases)
 * to reliably detect when both cards have been processed.
 */
export const checkAndFireBothCardsUploadedToast = ({
  isFrontCardProcessed,
  isBackCardProcessed,
  toastTimeoutRef,
  onBothCardsUploaded,
}: BothCardsUploadedCheckParams): void => {
  if (isFrontCardProcessed && isBackCardProcessed) {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current)
    }
    // Timeout to allow the individual card toast to complete its animation cycle before showing the "both cards" toast
    toastTimeoutRef.current = setTimeout(() => {
      onBothCardsUploaded()
    }, BOTH_CARDS_UPLOADED_TOAST_DELAY_MS)
  }
}
